import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { imageUrl } from "@/lib/api";
import BottomNav from "@/components/BottomNav";
import BrandMark from "@/components/BrandMark";
import Chip from "@/components/Chip";
import { IDS } from "@/constants/testIds";
import { Plus, X, Check, Pencil } from "lucide-react";

const CATEGORIES = ["all", "set", "shirt", "t-shirt", "jeans", "trousers", "kurta", "saree", "dress", "jacket", "footwear", "earrings", "necklace", "ring", "bracelet", "watch", "belt", "bag", "sunglasses", "scarf", "dupatta"];
const FORMALITIES = ["all", "casual", "office", "formal", "party", "traditional"];
const SEASON_MODES = ["all", "in-season", "out-of-season"];

export default function Closet() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [currentSeason, setCurrentSeason] = useState("");
  const [uploading, setUploading] = useState(false);
  const [cat, setCat] = useState("all");
  const [form, setForm] = useState("all");
  const [seasonMode, setSeasonMode] = useState("all");
  const [editing, setEditing] = useState(null);
  const [celebrate, setCelebrate] = useState(false);

  const load = async () => {
    const { data } = await api.get("/closet");
    setItems(data.items);
    setCurrentSeason(data.current_season || "");
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/closet/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setItems((prev) => [data.item, ...prev]);
      toast.success(`Added ${data.item.color} ${data.item.category}`);
      if (data.unlocked && data.closet_size === 5) {
        setCelebrate(true);
      }
    } catch (err) {
      toast.error("Upload failed — try a JPG or PNG");
    } finally {
      setUploading(false);
    }
  };

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (cat !== "all" && i.category !== cat) return false;
      if (form !== "all" && i.formality !== form) return false;
      if (seasonMode === "in-season" && i.in_season === false) return false;
      if (seasonMode === "out-of-season" && i.in_season !== false) return false;
      return true;
    });
  }, [items, cat, form, seasonMode]);

  const saveEdit = async () => {
    if (!editing) return;
    const { item_id, ...rest } = editing;
    const { data } = await api.patch(`/closet/${item_id}`, rest);
    setItems((prev) => prev.map((it) => (it.item_id === item_id ? data : it)));
    setEditing(null);
    toast.success("Updated");
  };

  const removeItem = async (id) => {
    await api.delete(`/closet/${id}`);
    setItems((prev) => prev.filter((it) => it.item_id !== id));
  };

  const [rebalancing, setRebalancing] = useState(false);
  const [rebalanceChanges, setRebalanceChanges] = useState(null);
  const rebalanceSeasons = async () => {
    setRebalancing(true);
    try {
      const { data } = await api.post("/closet/rebalance-seasons");
      if (data.updated > 0) {
        toast.success(`Re-tagged ${data.updated} item${data.updated === 1 ? "" : "s"}`);
        setRebalanceChanges(data.changes || []);
        await load();
      } else {
        toast.message("All items already have correct tags");
      }
    } catch (err) {
      toast.error("Couldn't refresh — try again");
    } finally {
      setRebalancing(false);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-[#FBF9F6] grain" style={{ paddingBottom: "calc(6rem + env(safe-area-inset-bottom, 0.75rem))" }}>
      <div className="max-w-md mx-auto px-6 pt-12">
        <BrandMark className="mb-4" />
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-stone-500">
              Wardrobe
            </div>
            <h1 className="font-[Cormorant_Garamond] text-4xl leading-tight tracking-tight text-stone-900">
              Your closet
            </h1>
          </div>
          <label className="h-12 w-12 rounded-full bg-[#6B7A54] text-white flex items-center justify-center shadow-[0_16px_40px_rgba(107, 122, 84,0.25)] cursor-pointer active:scale-[0.97] transition-transform">
            <Plus size={20} strokeWidth={1.5} />
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onUpload}
              disabled={uploading}
              data-testid={IDS.closetUploadInput}
            />
          </label>
        </div>
        <p className="text-sm font-[Manrope] text-stone-600 mb-6">
          {items.length} item{items.length === 1 ? "" : "s"} · we tag each piece automatically
        </p>

        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-6 px-6 pb-1 mb-3">
          {CATEGORIES.map((c) => (
            <Chip key={c} label={c} small active={cat === c} onClick={() => setCat(c)} testId={`cat-${c}`} />
          ))}
        </div>
        <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-6 px-6 pb-1">
          {FORMALITIES.map((c) => (
            <Chip key={c} label={c} small active={form === c} onClick={() => setForm(c)} testId={`form-${c}`} />
          ))}
        </div>
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar -mx-6 px-6 pb-1">
          {SEASON_MODES.map((s) => (
            <Chip
              key={s}
              label={s === "in-season" && currentSeason ? `In season · ${currentSeason}` : s.replaceAll("-", " ")}
              small
              active={seasonMode === s}
              onClick={() => setSeasonMode(s)}
              testId={`season-mode-${s}`}
            />
          ))}
        </div>

        {uploading && (
          <div className="mt-6 bg-white rounded-2xl p-4 border border-stone-100 text-sm font-[Manrope] text-stone-600">
            Tagging your item… this takes a few seconds.
          </div>
        )}

        <div data-testid={IDS.closetGrid} className="mt-6 grid grid-cols-2 gap-4">
          {filtered.map((it) => (
            <div key={it.item_id} data-testid={IDS.closetItem(it.item_id)} className="group relative rounded-2xl overflow-hidden bg-white border border-stone-100 shadow-[0_2px_8px_rgba(28,25,23,0.04)]">
              <div className={`aspect-[3/4] overflow-hidden ${it.in_season === false ? "opacity-70" : ""}`}>
                <img src={imageUrl(it.storage_path)} alt={it.category} className="w-full h-full object-cover" />
              </div>
              {it.in_season === false && (
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-stone-900/70 backdrop-blur-sm text-white text-[9px] font-[Manrope] font-semibold tracking-wider uppercase">
                  Out of season
                </div>
              )}
              <div className="p-3">
                <div className="text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-[#6B7A54] truncate">
                  {it.category}
                </div>
                <div className="text-xs font-[Manrope] text-stone-600 truncate">
                  {it.color} · {it.formality}
                </div>
              </div>
              <button
                onClick={() => setEditing({ ...it })}
                className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center text-stone-700 shadow-sm"
              >
                <Pencil size={14} strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>

        {items.length === 0 && !uploading && (
          <div className="mt-10 text-center">
            <p className="font-[Cormorant_Garamond] text-2xl italic text-stone-500">
              Your wardrobe is a blank canvas.
            </p>
            <p className="mt-2 text-sm font-[Manrope] text-stone-500">
              Tap the + to add your first piece.
            </p>
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex items-end justify-center">
          <div className="w-full max-w-md bg-[#FBF9F6] rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="font-[Cormorant_Garamond] text-2xl italic">Edit tags</div>
              <button onClick={() => setEditing(null)} className="h-9 w-9 rounded-full border border-stone-200 flex items-center justify-center">
                <X size={16} />
              </button>
            </div>
            <EditField label="Category" value={editing.category} onChange={(v)=>setEditing({...editing, category: v})} />
            <EditField label="Color" value={editing.color} onChange={(v)=>setEditing({...editing, color: v})} />
            <EditField label="Pattern" value={editing.pattern} onChange={(v)=>setEditing({...editing, pattern: v})} />
            <EditField label="Fabric" value={editing.fabric} onChange={(v)=>setEditing({...editing, fabric: v})} />
            <div className="mb-3">
              <div className="text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-stone-500 mb-2">Formality</div>
              <div className="flex flex-wrap gap-2">
                {["casual","office","formal","party","traditional"].map(f=>(
                  <button key={f} onClick={()=>setEditing({...editing, formality:f})}
                    className={`px-3 py-1.5 rounded-full text-xs font-[Manrope] capitalize ${editing.formality===f?"bg-stone-900 text-white":"bg-white border border-stone-200 text-stone-700"}`}>{f}</button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={()=>removeItem(editing.item_id)} className="flex-1 py-3 rounded-full border border-stone-300 text-stone-600 font-[Manrope] text-sm">Delete</button>
              <button onClick={saveEdit} className="flex-1 py-3 rounded-full bg-[#6B7A54] text-white font-[Manrope] text-sm flex items-center justify-center gap-2">
                <Check size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {celebrate && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-md z-50 flex items-center justify-center p-6">
          <div className="bg-[#FBF9F6] rounded-3xl p-8 text-center max-w-sm">
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54]">Unlocked</div>
            <h2 className="mt-3 font-[Cormorant_Garamond] text-4xl italic text-stone-900">
              Five pieces.<br />Let&rsquo;s style you.
            </h2>
            <p className="mt-4 font-[Manrope] text-sm text-stone-600 leading-relaxed">
              Your closet is ready for its first outfit suggestion. Keep adding pieces to unlock more variety.
            </p>
            <button
              onClick={() => { setCelebrate(false); navigate("/home"); }}
              className="mt-6 w-full bg-[#6B7A54] text-white rounded-full py-4 font-[Manrope] font-medium"
            >
              Style me now
            </button>
          </div>
        </div>
      )}

      {rebalanceChanges && rebalanceChanges.length > 0 && (
        <div
          data-testid="rebalance-review-modal"
          className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-4"
        >
          <div className="bg-[#FBF9F6] w-full max-w-md rounded-3xl p-6 max-h-[85vh] overflow-y-auto">
            <div className="text-[10px] font-[Manrope] font-bold tracking-[0.3em] uppercase text-[#6B7A54] mb-1">
              Review
            </div>
            <h2 className="font-[Cormorant_Garamond] text-3xl leading-tight text-stone-900 mb-1">
              We re-tagged {rebalanceChanges.length} item{rebalanceChanges.length === 1 ? "" : "s"}
            </h2>
            <p className="text-sm font-[Manrope] text-stone-600 mb-4">
              Tap any card to fine-tune, or dismiss if these look right.
            </p>
            <div className="space-y-3">
              {rebalanceChanges.map((c) => (
                <div key={c.item_id} className="flex items-center gap-3 bg-white/70 border border-stone-200 rounded-2xl p-3">
                  {c.storage_path && (
                    <img
                      src={imageUrl(c.storage_path)}
                      alt=""
                      className="w-14 h-14 rounded-xl object-cover shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0 text-xs font-[Manrope]">
                    {c.category_changed && (
                      <div className="text-stone-700">
                        <span className="text-stone-400 line-through">{c.old_category}</span>
                        <span className="mx-1">→</span>
                        <span className="text-[#6B7A54] font-semibold">{c.new_category}</span>
                      </div>
                    )}
                    {c.season_changed && (
                      <div className="text-stone-700 mt-0.5">
                        <span className="text-stone-400 line-through">{(c.old_season || []).join(", ")}</span>
                        <span className="mx-1">→</span>
                        <span className="text-[#6B7A54] font-semibold">{(c.new_season || []).join(", ")}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const item = items.find((x) => x.item_id === c.item_id);
                      if (item) setEditing({ ...item });
                      setRebalanceChanges(null);
                    }}
                    className="text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-[#6B7A54] shrink-0"
                  >
                    Edit
                  </button>
                </div>
              ))}
            </div>
            <button
              data-testid="rebalance-review-dismiss"
              onClick={() => setRebalanceChanges(null)}
              className="mt-6 w-full bg-[#6B7A54] text-white rounded-full py-3 font-[Manrope] font-medium"
            >
              Looks good
            </button>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}

const EditField = ({ label, value, onChange }) => (
  <div className="mb-3">
    <div className="text-[10px] font-[Manrope] font-bold tracking-[0.2em] uppercase text-stone-500 mb-1">{label}</div>
    <input value={value||""} onChange={(e)=>onChange(e.target.value)} className="input" />
  </div>
);
